import { useEffect, useState, useCallback } from 'react';
import './App.css';
import { PageContainer } from './components/PageContainer/pageContainer';
import { MsalProvider } from '@azure/msal-react';
import { getIdToken, initializeMsal, msalResult, setupTokenRefresh, testTokenRefresh } from './authConfig';
import CookieConsent, { Cookies } from 'react-cookie-consent';
import ConsentForm from './components/ConsentForm/ConsentForm';
import { LinearProgress } from '@mui/material';
import { GetUsersIdentity } from './services/ApiService';
import { useLanguage } from './components/Language/LanguageContext';
import { useTranslation } from 'react-i18next';
import { logger } from './utils/logger';

function App() {
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [initialConsentVisible, setInitialConsentVisible] = useState(true);
  const [lngpreference, setLngPreference] = useState<boolean>();
  const [isInitialized, setIsInitialized] = useState(false);
  const {language, setGlobalLanguage} = useLanguage();
  const { i18n } = useTranslation();

  const { theme } = msalResult;

  const redirectToWebsite = (url: any) => {
    window.open(url, "_blank");
  }

  const GetUserIdentityInformation = useCallback(async () => {
    try {
      const tokenAccess = await getIdToken();
      const userIdentity = await GetUsersIdentity(tokenAccess);
      const preferredLanguage = userIdentity?.data?.preferredLanguage;

      if (preferredLanguage === 'sv' || preferredLanguage === 'no') {
        setGlobalLanguage(preferredLanguage);
        setLngPreference(true);
      } else {
        setGlobalLanguage('en');
        setLngPreference(false);
      }
    } catch (error) {
      logger.error('Error fetching user identity:', error);
    }
  }, [setGlobalLanguage]);

  // Initialize MSAL
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeMsal();
        
        // Delay to ensure initialization is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set login hint from localStorage if available to help with automatic login
        if (msalResult.instance) {
          // Check if we have previously saved account info
          // SECURITY: Use sessionStorage instead of localStorage for user info
          const rememberedUser = sessionStorage.getItem('rememberedUser');
          if (rememberedUser) {
            try {
              const userInfo = JSON.parse(rememberedUser);
              if (userInfo && userInfo.username) {
                // Set login hint for easier re-authentication
                const existingAuthConfig = msalResult.instance.getConfiguration().auth;
                msalResult.instance.getConfiguration().auth = {
                  ...existingAuthConfig,
                  loginHint: userInfo.username
                };
              }
            } catch (parseError) {
              sessionStorage.removeItem('rememberedUser');
            }
          }
        }
        
        // Handle redirect callback if this is a redirect return
        if (msalResult.instance) {
          try {
            // This will process the redirect response
            const response = await msalResult.instance.handleRedirectPromise();
            
            // If we got a successful response, save user info for future sessions
            if (response && response.account) {
              const accountToRemember = {
                username: response.account.username,
                name: response.account.name,
                environment: response.account.environment,
                homeAccountId: response.account.homeAccountId,
                tenantId: response.account.tenantId
              };
              sessionStorage.setItem('rememberedUser', JSON.stringify(accountToRemember));
            }
            
            // Check if we need to redirect back to a saved page
            const redirectPath = sessionStorage.getItem('redirectAfterLogin');
            if (redirectPath && redirectPath !== window.location.href) {
              // SECURITY FIX: Validate redirect URL to prevent open redirect attacks
              const currentOrigin = window.location.origin;
              const isValidRedirect = redirectPath.startsWith(currentOrigin) || 
                                      redirectPath.startsWith('/');
              
              sessionStorage.removeItem('redirectAfterLogin');
              
              if (isValidRedirect) {
                window.location.href = redirectPath;
                return;
              }
              // Invalid redirect URL - ignore silently
            }
          } catch (redirectError) {
            logger.error('Error handling redirect:', redirectError);
          }
        }
        
        setLoading(false);
        setIsInitialized(true);
        
        // Set up token refresh once MSAL is initialized
        setupTokenRefresh();
        
        // Start a token refresh after initialization is complete
        setTimeout(() => {
          if (process.env.NODE_ENV !== 'production') {
            testTokenRefresh();
          }
        }, 3000);
      } catch (error) {
        logger.error('Error during initialization:', error);
        setLoading(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (isInitialized) {
      const timer = setTimeout(() => {
        GetUserIdentityInformation();
      }, 5000); 
      return () => clearTimeout(timer);
    }
  }, [isInitialized, GetUserIdentityInformation]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'en'; 

    if (savedLanguage === 'no' || savedLanguage === 'sv') {
      setLngPreference(true);
    } else {
      setLngPreference(false);
    }

    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    if (language === 'no' || language === 'sv') {
      setLngPreference(true);
      localStorage.setItem('preferredLanguage', language); 
    } else {
      setLngPreference(false);
      localStorage.setItem('preferredLanguage', 'en');
    }
  }, [language]); 

  if (loading) {
    return( 
      <div style={{display:'flex', justifyContent:'center', flexDirection:'row'}}>
        <div style={{width:'100%'}}>
          <LinearProgress/>
        </div>
      </div>
    );
  }
   
  if (!msalResult.instance) {
    return <div>Error initializing MSAL</div>;
  }    

  const handleCloseConsentForm = () => {
    return setOpen(false);
  }

  const handleAccept = () => {
    Cookies.set('myAppCookieConsent', 'true', { expires: 150 });
    setInitialConsentVisible(false);
  };

  const handleDecline = () => {
    Cookies.remove('functional');
    Cookies.remove('marketing');
    Cookies.set('myAppCookieConsent', 'false', { expires: 150 });
    setInitialConsentVisible(false);
  };

  return (
    <MsalProvider instance={msalResult.instance}>
      {
        initialConsentVisible && (
          <CookieConsent
            location="bottom"
            buttonText="Confirm"
            declineButtonText=""
            cookieName="myAppCookieConsent"
            style={{ background: "#0c1732", color: "#FFF", maxWidth:'600px', width:'90%', left:'1%', borderRadius:'2%'}}
            buttonStyle={{ background: "#09b885", color: "#ffff", margin:'0px 10px 20px 10px', fontSize: "14px", borderRadius:'10%' }}
            declineButtonStyle={{ fontSize: "14px", display:'none', background:'#f32b2b', margin:'0px 10px 20px 10px', borderRadius:'10%' }}
            expires={150}
            enableDeclineButton
            onDecline={() => {
              Cookies.remove('functional');
              Cookies.remove('marketing');
            }}
            onAccept={() => {
              Cookies.remove('marketing');
            }}
          >
            <p style={{ marginRight:'auto', color: 'white', font:`normal normal 300 14px/16px ${theme?.Style?.font}`, margin:'0px', paddingLeft:'1%' }}>
              We use first-party cookies for the website to function the right way, and they do not collect any of your personally identifiable data. Mw Cloud related web pages do not use third party cookies. More information on how we use cookies is found here
            </p>
            <div style={{display:'flex', justifyContent:'space-between', marginTop:'3%'}}>
              <p onClick={()=>{redirectToWebsite( lngpreference ? theme?.Footer?.CookiePolicyNo : theme?.Footer?.CookiePolicy)}} style={{cursor:'pointer', marginRight:'auto', margin:'0px', color: '#09b885', font:`normal normal 300 14px/20px ${theme?.Style?.font}`, paddingLeft:'1%' }}>Cookie Policy</p>
            </div>
          </CookieConsent>
        )
      }
      { open && (
        <ConsentForm handleopen={open} closeconsentform={handleCloseConsentForm} handleAccept={handleAccept} handleDecline={handleDecline} />
      )}
      <div className="App">
        <PageContainer />
      </div>
    </MsalProvider>
  );
}

export default App;
